<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import ExternalLinkIcon from '@/assets/icons/external-link.svg?component'
import HelpIcon from '@/assets/icons/help.svg?component'
import { BoxComponent, FlexComponent, TooltipComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{
  hasDynamicUrls?: boolean
  tabs: {
    label: string
    path?: string
    href?: string
    tooltipSlotName?: string
  }[]
}>()
const slots = useSlots()
const route = useRoute()
const router = useRouter()

const activeTab = ref(0)

const defaultInternalTabIndex = () => props.tabs.findIndex((tab) => tab.path && !tab.href)

const tabSlots = computed(() => props.tabs.map((_, i) => `tab${i + 1}`))

watch(
  () => route.params.tab,
  (tabParam) => {
    if (!props.hasDynamicUrls) {
      activeTab.value = 0
      return
    }

    const resolvedTabParam = tabParam

    const matchedTab = props.tabs.find((item) => item.path === resolvedTabParam)
    if (matchedTab?.href) {
      const internalIndex = defaultInternalTabIndex()
      activeTab.value = internalIndex !== -1 ? internalIndex : 0
      window.location.assign(matchedTab.href)
      return
    }

    const index = props.tabs.findIndex((item) => item.path === resolvedTabParam)
    const internalIndex = defaultInternalTabIndex()
    activeTab.value = index !== -1 ? index : internalIndex !== -1 ? internalIndex : 0
  },
  { immediate: true },
)

watch(activeTab, async (newValue) => {
  if (!props.hasDynamicUrls) {
    return
  }
  const tab = props.tabs[newValue]
  if (!tab?.path || tab.href) {
    return
  }
  if (route.params.tab === tab.path) {
    return
  }
  await router.push({ params: { ...route.params, tab: tab.path } })
})
</script>

<template>
  <div class="tabs">
    <FlexComponent class="tabs__head">
      <template v-for="(tab, index) in tabs" :key="tab.label">
        <a v-if="tab.href" :href="tab.href" class="tabs__head__item tabs__head__item--external">
          <FlexComponent :gap="8" align="center">
            <TypographyComponent variant="title-large">
              {{ tab.label }}
            </TypographyComponent>
            <el-icon :size="16" color="var(--secondary-48)" class="tabs__external-icon">
              <ExternalLinkIcon />
            </el-icon>
          </FlexComponent>
        </a>
        <button v-else type="button" :class="['tabs__head__item', { 'is-active': activeTab === index }]" @click="activeTab = index">
          <FlexComponent :gap="8" align="center">
            <TypographyComponent variant="title-large">
              {{ tab.label }}
            </TypographyComponent>
            <TooltipComponent
              v-if="tab.tooltipSlotName && slots[tab.tooltipSlotName]"
              placement="bottom"
              effect="light"
              :show-arrow="false"
              class="tabs__tooltip"
            >
              <el-icon :size="18" class="tooltip-handle">
                <HelpIcon />
              </el-icon>
              <template #content>
                <slot :name="tab.tooltipSlotName" />
              </template>
            </TooltipComponent>
          </FlexComponent>
        </button>
      </template>
    </FlexComponent>
    <BoxComponent class="tabs__content" :p="24">
      <BoxComponent
        v-for="(slotName, index) in tabSlots"
        :key="slotName"
        :class="['tabs__content__item', { 'is-active': activeTab === index }]"
      >
        <slot :name="slotName" />
      </BoxComponent>
    </BoxComponent>
  </div>
</template>

<style scoped>
.tabs__head {
  margin-bottom: -2px;
  border-bottom: 1px solid var(--inverted);
}
.tabs__head__item {
  display: inline-flex;
  align-items: center;
  padding: 16px 24px;
  border-right: 1px solid transparent;
  border-left: 1px solid var(--secondary-8);
  border-top: 1px solid var(--secondary-8);
  border-bottom: 1px solid var(--secondary-8);
  background-color: var(--secondary-2);
  border-radius: 0;
  cursor: pointer;
  transition: all var(--transition) ease;
  text-decoration: none;
  color: inherit;

  &.is-active {
    background: var(--inverted);
    border-bottom-color: transparent;
  }

  &--external:hover {
    background-color: var(--secondary-4);
  }

  &:first-child {
    border-radius: 16px 0 0 0;
  }

  &:last-child {
    border-right-color: var(--secondary-8);
    border-radius: 0 16px 0 0;
  }
}

.tabs__external-icon :deep(path) {
  fill: currentColor;
}

.tabs__content {
  width: 100%;
  background: var(--inverted);
  border-radius: 0 16px 16px 16px;
  border: 1px solid var(--secondary-8);
}

.tabs__content__item {
  display: none;

  &.is-active {
    display: block;
    min-width: 100%;
  }
}

.tabs__tooltip {
  display: flex;
}
</style>
